import { MutableRefObject, memo, useCallback, useMemo, useRef } from "react";
import { PromptSliders, PromptSlidersHandle } from "./PromptSliders";
import { ParameterToggles } from "./ParameterToggles";
import { StemControls } from "./StemControls";
import { TransportBar } from "./TransportBar";
import { WaveformVisualizer } from "@/components/canvas/WaveformVisualizer";
import { ActivePromptDisplay } from "@/components/canvas/ActivePromptDisplay";
import { LyriaState, MusicConfig, WeightedPrompt } from "@/lib/lyria-client";
import { PromptMode } from "@/hooks/use-lyria";
import { Activity, Headphones, Radio, SlidersHorizontal } from "lucide-react";

interface InstrumentPanelProps {
  lyriaState: LyriaState;
  audioLevelRef: MutableRefObject<number>;
  analyserNode: AnalyserNode | null;
  config: MusicConfig;
  currentPrompts: WeightedPrompt[];
  volume: number;
  onSetPromptsWithMode: (prompts: WeightedPrompt[], mode: PromptMode) => void;
  onUpdateConfig: (config: Partial<MusicConfig>, needsReset?: boolean) => void;
  onVolumeChange: (volume: number) => void;
  onPrimeAudio: () => void;
  onPlay: () => void;
  onPause: () => void;
  onResetContext: () => void;
}

export const InstrumentPanel = memo(function InstrumentPanel({
  lyriaState,
  audioLevelRef,
  analyserNode,
  config,
  currentPrompts,
  volume,
  onSetPromptsWithMode,
  onUpdateConfig,
  onVolumeChange,
  onPrimeAudio,
  onPlay,
  onPause,
  onResetContext,
}: InstrumentPanelProps) {
  const slidersRef = useRef<PromptSlidersHandle>(null);

  const isActive =
    lyriaState === "playing" ||
    lyriaState === "starting" ||
    lyriaState === "buffering";
  const isFirstRun = currentPrompts.length === 0 && !isActive;

  const statusLabel = useMemo(() => {
    switch (lyriaState) {
      case "playing":
        return "Live and moving";
      case "starting":
        return "Starting the stream";
      case "buffering":
        return "Catching the next bar";
      case "paused":
        return "Paused in place";
      case "connecting":
        return "Connecting to Lyria";
      default:
        return "Ready for a new scene";
    }
  }, [lyriaState]);

  const promptSummary = useMemo(() => {
    if (currentPrompts.length === 0) {
      return "No prompt loaded";
    }

    return currentPrompts
      .slice(0, 3)
      .map((prompt) => prompt.text)
      .join(" • ");
  }, [currentPrompts]);

  const promptCountLabel = useMemo(() => {
    if (currentPrompts.length === 0) return "No active prompts";
    return `${currentPrompts.length} active ${currentPrompts.length === 1 ? "prompt" : "prompts"}`;
  }, [currentPrompts.length]);

  const handlePlay = useCallback(async () => {
    onPrimeAudio();
    const flushed = await slidersRef.current?.flushInput();
    if (!flushed) {
      onPlay();
    }
  }, [onPlay, onPrimeAudio]);

  return (
    <div className="relative h-full w-full overflow-x-hidden overflow-y-auto bg-[#050505] lg:overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-full w-full max-w-[1720px] flex-col px-4 pb-28 pt-[4.75rem] sm:px-6 lg:h-full lg:px-8 lg:pb-20 lg:pt-[4.25rem]">
        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="flex min-h-0 flex-col gap-6">
            <div className="grid min-h-0 flex-1 gap-4 lg:grid-rows-[minmax(300px,1fr)_minmax(220px,auto)]">
              <div className="relative min-h-[470px] overflow-hidden rounded-lg border border-white/10 bg-black/55 shadow-2xl shadow-black/35 sm:min-h-[430px] lg:min-h-[300px]">
                <WaveformVisualizer
                  isPlaying={isActive}
                  audioLevelRef={audioLevelRef}
                  analyserNode={analyserNode}
                  className="absolute inset-0 h-full w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/88" />

                <div className="relative flex h-full flex-col justify-between p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/55 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-white/70">
                        <Radio className={`h-3.5 w-3.5 ${isActive ? "text-emerald-300" : "text-white/40"}`} />
                        <span>{statusLabel}</span>
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/50 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-white/52">
                        <Headphones className="h-3.5 w-3.5" />
                        <span>{promptCountLabel}</span>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-black/50 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-white/45">
                      <Activity className="h-3.5 w-3.5" />
                      <span>{isActive ? "Reactive Visualizer" : "Preview Surface"}</span>
                    </div>
                  </div>

                  {isFirstRun ? (
                    <div className="mx-auto w-full max-w-3xl space-y-5 pb-2">
                      <div className="space-y-2">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-cyan-200/75">Start here</p>
                        <h1 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">
                          Describe the music you want to hear.
                        </h1>
                        <p className="max-w-2xl text-sm leading-6 text-white/62">
                          Use plain language: genre, mood, tempo, instruments, place, or artist references. SonicForge
                          translates it into Lyria-safe music prompts before anything is sent to the audio model.
                        </p>
                      </div>
                      <div className="rounded-lg border border-white/12 bg-white/[0.07] p-4 shadow-2xl shadow-black/30 backdrop-blur-xl">
                        <PromptSliders
                          ref={slidersRef}
                          prompts={currentPrompts}
                          onSetPromptsWithMode={onSetPromptsWithMode}
                          onUpdateConfig={onUpdateConfig}
                          onPrimeAudio={onPrimeAudio}
                          onPlay={onPlay}
                          autoFocus
                        />
                      </div>
                      <div className="grid gap-2 text-xs text-white/48 sm:grid-cols-3">
                        <div className="rounded-md border border-white/8 bg-black/35 px-3 py-2">
                          late night deep house with soft pads
                        </div>
                        <div className="rounded-md border border-white/8 bg-black/35 px-3 py-2">
                          sparse ambient piano in a huge room
                        </div>
                        <div className="rounded-md border border-white/8 bg-black/35 px-3 py-2">
                          fast drum and bass with warm sub
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-4xl space-y-3">
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-cyan-200/75">Current Blend</p>
                          <h1 className="mt-2 text-2xl font-semibold leading-tight text-white sm:text-3xl">
                            {isActive ? "Live stream" : "Ready for input"}
                          </h1>
                          {currentPrompts.length > 0 && (
                            <p className="mt-2 line-clamp-2 max-w-[34rem] text-sm leading-5 text-white/62">
                              {promptSummary}
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-1">
                          <div className="rounded-md border border-white/10 bg-black/50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">BPM</p>
                            <p className="mt-1 text-lg font-mono text-white">{config.bpm ?? 120}</p>
                          </div>
                          <div className="rounded-md border border-white/10 bg-black/50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">Density</p>
                            <p className="mt-1 text-lg font-mono text-white">{Math.round((config.density ?? 0.65) * 100)}</p>
                          </div>
                          <div className="rounded-md border border-white/10 bg-black/50 px-3 py-2">
                            <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">Bright</p>
                            <p className="mt-1 text-lg font-mono text-white">{Math.round((config.brightness ?? 0.7) * 100)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]">
                {!isFirstRun && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.055] p-4 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-5">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/42">Prompt</p>
                        <h2 className="mt-1 text-xl font-medium text-white">
                          Scene controls
                        </h2>
                      </div>
                    </div>

                    <PromptSliders
                      ref={slidersRef}
                      prompts={currentPrompts}
                      onSetPromptsWithMode={onSetPromptsWithMode}
                      onUpdateConfig={onUpdateConfig}
                      onPrimeAudio={onPrimeAudio}
                      onPlay={onPlay}
                    />
                  </div>
                )}

                <div className={`rounded-lg border border-white/10 bg-white/[0.055] p-4 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-5 ${isFirstRun ? "lg:col-span-2" : ""}`}>
                  <div className="space-y-6">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.16em] text-white/42">Shape</p>
                      <h2 className="mt-1 text-xl font-medium text-white">
                        Texture and stems
                      </h2>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-white/38">
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Texture
                      </div>
                      <ParameterToggles config={config} onUpdateConfig={onUpdateConfig} />
                    </div>

                    <div className="h-px bg-white/8" />

                    <div className="space-y-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-white/38">Stems</p>
                      <StemControls config={config} onUpdateConfig={onUpdateConfig} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <aside className="flex min-h-0 flex-col gap-5">
            <div className="rounded-lg border border-white/10 bg-white/[0.055] p-4 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/42">Monitor</p>
                  <h2 className="mt-1 text-xl font-medium text-white">
                    {currentPrompts.length > 0 ? "Active blend" : "Idle"}
                  </h2>
                </div>
                <div className="rounded-md border border-white/10 bg-black/30 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-white/46">
                  {isActive ? "Live" : "Idle"}
                </div>
              </div>
              <p className="mt-3 line-clamp-3 text-sm leading-5 text-white/58">
                {currentPrompts.length > 0 ? promptSummary : "No active synthesis"}
              </p>
            </div>

            <div className="min-h-0 flex-1 rounded-lg border border-white/10 bg-white/[0.055] p-3 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-4">
              <ActivePromptDisplay prompts={currentPrompts} isPlaying={isActive} />
            </div>
          </aside>
        </div>
      </div>

      <TransportBar
        lyriaState={lyriaState}
        config={config}
        volume={volume}
        onPlay={handlePlay}
        onPause={onPause}
        onResetContext={onResetContext}
        onVolumeChange={onVolumeChange}
        onUpdateConfig={onUpdateConfig}
      />
    </div>
  );
});
